import React, { useId, useMemo } from "react"

const WIDTH = 720
const HEIGHT = 220
const PAD = { top: 16, right: 16, bottom: 8, left: 16 }

/**
 * Build a smooth cubic path through points.
 */
const buildSmoothPath = points => {
  if (!points.length) return ""
  if (points.length === 1) {
    const { x, y } = points[0]
    return `M ${x} ${y}`
  }

  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? i : i - 1]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] || p2
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`
  }
  return d
}

/** Evenly spaced indexes, always including first + last. */
const pickLabelIndexes = (count, maxLabels = 7) => {
  if (count <= 0) return []
  if (count <= maxLabels) {
    return Array.from({ length: count }, (_, i) => i)
  }
  const indexes = new Set([0, count - 1])
  const step = (count - 1) / (maxLabels - 1)
  for (let i = 1; i < maxLabels - 1; i += 1) {
    indexes.add(Math.round(i * step))
  }
  return [...indexes].sort((a, b) => a - b)
}

/**
 * Multi-series line chart for Admin Platform Overview.
 * Plot in SVG; X labels in a padded HTML row aligned to data points.
 */
const PlatformOverviewChart = ({
  labels = [],
  series = [],
  className = "",
}) => {
  const reactId = useId()
  const gradientPrefix = `po-grad-${reactId.replace(/:/g, "")}`

  const chart = useMemo(() => {
    const plotW = WIDTH - PAD.left - PAD.right
    const plotH = HEIGHT - PAD.top - PAD.bottom
    const pointCount = Math.max(labels.length, 1)

    const allValues = series.flatMap(s =>
      (s.values || []).map(v => Number(v) || 0),
    )
    const maxRaw = Math.max(...allValues, 0)
    const max = maxRaw <= 0 ? 1 : maxRaw * 1.12

    const toPoint = (value, index) => {
      const x =
        PAD.left +
        (pointCount === 1 ? plotW / 2 : (index / (pointCount - 1)) * plotW)
      const y =
        PAD.top + plotH - (Math.max(0, Number(value) || 0) / max) * plotH
      return { x, y }
    }

    const builtSeries = series.map(s => {
      const values = Array.isArray(s.values) ? s.values : []
      const points = values.map((value, index) => toPoint(value, index))
      const line = buildSmoothPath(points)
      const area =
        points.length > 0
          ? `${line} L ${points[points.length - 1].x.toFixed(1)} ${(PAD.top + plotH).toFixed(1)} L ${points[0].x.toFixed(1)} ${(PAD.top + plotH).toFixed(1)} Z`
          : ""
      return {
        key: s.key,
        color: s.color,
        line,
        area,
        points,
      }
    })

    const labelIndexes = pickLabelIndexes(labels.length, 7)
    const axisLabels = labelIndexes.map(index => {
      const x = toPoint(0, index).x
      return {
        index,
        label: labels[index] || "",
        // Align with SVG plot coordinates (includes side padding)
        left: (x / WIDTH) * 100,
      }
    })

    const gridYs = [0, 1, 2, 3, 4].map(i => PAD.top + (plotH / 4) * i)

    return { builtSeries, axisLabels, gridYs }
  }, [labels, series])

  const hasData = series.some(s => (s.values || []).length > 0)

  if (!hasData) {
    return (
      <div
        className={`admin-dashboard__chart admin-dashboard__chart--empty ${className}`.trim()}
      >
        <p className="admin-dashboard__chart-empty">
          No overview data for this period.
        </p>
      </div>
    )
  }

  return (
    <div className={`admin-dashboard__chart ${className}`.trim()}>
      <svg
        className="admin-dashboard__chart-svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Platform overview chart"
      >
        <defs>
          {chart.builtSeries.map(item => (
            <linearGradient
              key={`${gradientPrefix}-${item.key}`}
              id={`${gradientPrefix}-${item.key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={item.color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={item.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {chart.gridYs.map((y, index) => (
          <line
            key={`grid-${index}`}
            x1={PAD.left}
            y1={y}
            x2={WIDTH - PAD.right}
            y2={y}
            className="admin-dashboard__chart-grid"
          />
        ))}

        {chart.builtSeries.map(item => (
          <path
            key={`area-${item.key}`}
            d={item.area}
            fill={`url(#${gradientPrefix}-${item.key})`}
            stroke="none"
          />
        ))}

        {chart.builtSeries.map(item => (
          <path
            key={`line-${item.key}`}
            d={item.line}
            fill="none"
            stroke={item.color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {chart.builtSeries.map(item =>
          item.points.map((point, index) => (
            <circle
              key={`dot-${item.key}-${index}`}
              cx={point.x}
              cy={point.y}
              r="3.5"
              fill={item.color}
              stroke="#fff"
              strokeWidth="1.5"
            />
          )),
        )}
      </svg>

      <div className="admin-dashboard__chart-axis" aria-hidden="true">
        {chart.axisLabels.map((item, position) => {
          const isFirst = position === 0
          const isLast = position === chart.axisLabels.length - 1
          return (
            <span
              key={`label-${item.index}`}
              className={`admin-dashboard__chart-tick${
                isFirst
                  ? " admin-dashboard__chart-tick--start"
                  : isLast
                    ? " admin-dashboard__chart-tick--end"
                    : ""
              }`}
              style={{ left: `${item.left}%` }}
              title={item.label}
            >
              {item.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

export default PlatformOverviewChart
